import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Tag, Badge, Button } from '@caos/ca-component-library';
import ReactMarkdown from 'react-markdown';
import { isRecentlyUpdated, formatDate, fetchSkillContent, getRelatedSkills, getRelatedMcpServers } from '../lib/marketplace';
import { useManifest } from '../context/ManifestContext';
import { useMcpRegistry } from '../context/McpRegistryContext';
import SkillCard from '../components/SkillCard';
import McpServerCard from '../components/McpServerCard';
import CodeBlock from '../components/CodeBlock';
import CopyButton from '../components/CopyButton';
import StarButton from '../components/StarButton';
import { Skeleton, SkeletonCodeBlock } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import usePageTitle from '../hooks/usePageTitle';
import './SkillDetailPage.css';

export default function SkillDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { skills, loading } = useManifest();
  const { servers } = useMcpRegistry();

  const [skillContent, setSkillContent] = useState<string | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentOpen, setContentOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'preview' | 'raw'>('preview');
  const [installTab, setInstallTab] = useState<string>('claude-code');

  const parseFrontmatter = (content: string) => {
    const match = content.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
    if (!match) return { frontmatter: null, body: content };
    return { frontmatter: match[1], body: match[2] };
  };

  const skill = skills.find((s) => s.id === id);
  usePageTitle(skill?.name);

  useEffect(() => {
    if (!contentOpen || skillContent !== null || !skill) return;
    setContentLoading(true);
    fetchSkillContent(skill.skill_md_path)
      .then(setSkillContent)
      .catch(() => setSkillContent(''))
      .finally(() => setContentLoading(false));
  }, [contentOpen, skill, skillContent]);

  if (loading) {
    return (
      <div className="skill-detail-page">
        <Skeleton width="120px" height="14px" />
        <div style={{ marginTop: 24 }}>
          <Skeleton width="40%" height="28px" />
          <Skeleton width="70%" height="16px" className="skeleton-mt-12" />
        </div>
        <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Skeleton width="100%" height="16px" />
          <Skeleton width="85%" height="16px" />
          <Skeleton width="60%" height="16px" />
        </div>
      </div>
    );
  }

  if (!skill) {
    return (
      <div className="skill-detail-page">
        <EmptyState
          title="Skill not found"
          description={`No skill with ID "${id}" exists in the registry.`}
          action={
            <Button size="sm" onClick={() => navigate('/')}>
              Back to marketplace
            </Button>
          }
        />
      </div>
    );
  }

  const recent = isRecentlyUpdated(skill);
  const isMcpUploaded = skill.tags.includes('mcp-uploaded');
  const displayTags = skill.tags.filter((t) => t !== 'mcp-uploaded');
  const relatedSkills = getRelatedSkills(skill.tags, skill.id, skills, 2);
  const relatedMcp = getRelatedMcpServers(skill.tags, skill.id, servers, 2);
  const hasRelated = relatedSkills.length > 0 || relatedMcp.length > 0;

  return (
    <div className="skill-detail-page">
      <nav className="skill-detail-breadcrumb">
        <Link to="/" className="skill-detail-back">
          &larr; Back to marketplace
        </Link>
      </nav>

      <header className="skill-detail-header">
        <div className="skill-detail-title-row">
          <h1 className="skill-detail-name">{skill.name}</h1>
          {recent && (
            <Badge
              size="sm"
              shape="capsule"
              style={{ '--badge-bg': 'var(--recently-updated-bg)' } as React.CSSProperties}
            >
              Recently Updated
            </Badge>
          )}
          {isMcpUploaded && (
            <Badge
              size="sm"
              shape="capsule"
              style={{ '--badge-bg': 'var(--ca-primary)' } as React.CSSProperties}
            >
              Uploaded via MCP
            </Badge>
          )}
          <Badge
            size="sm"
            shape="capsule"
            style={{ '--badge-bg': 'var(--skills-primary)' } as React.CSSProperties}
          >
            Skill
          </Badge>
          <StarButton itemId={skill.id} itemType="skill" />
        </div>
        <p className="skill-detail-description">{skill.description}</p>
      </header>

      <div className="skill-detail-content">
        <section className="skill-detail-section">
          <h2 className="skill-detail-section-title">Details</h2>
          <dl className="skill-detail-meta">
            <div className="skill-detail-meta-item">
              <dt>Owner</dt>
              <dd>{skill.owner}</dd>
            </div>
            <div className="skill-detail-meta-item">
              <dt>Version</dt>
              <dd>{skill.version}</dd>
            </div>
            <div className="skill-detail-meta-item">
              <dt>Last updated</dt>
              <dd>{formatDate(skill.last_updated)}</dd>
            </div>
            <div className="skill-detail-meta-item">
              <dt>Repository path</dt>
              <dd>
                <code>{skill.repo_path}</code>
              </dd>
            </div>
            <div className="skill-detail-meta-item">
              <dt>Skill file</dt>
              <dd>
                <code>{skill.skill_md_path}</code>
              </dd>
            </div>
            <div className="skill-detail-meta-item">
              <dt>Source</dt>
              <dd>
                <a
                  href={`https://bitbucket.org/ca-labs/claude-code-marketplace/src/main/${skill.repo_path}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="skill-detail-source-link"
                >
                  View on Bitbucket
                </a>
              </dd>
            </div>
          </dl>
        </section>

        {displayTags.length > 0 && (
          <section className="skill-detail-section">
            <h2 className="skill-detail-section-title">Tags</h2>
            <div className="skill-detail-tags">
              {displayTags.map((tag) => (
                <Tag key={tag} size="md" border>
                  {tag}
                </Tag>
              ))}
            </div>
          </section>
        )}

        <section className="skill-detail-section">
          <button className="skill-detail-collapse-toggle" onClick={() => setContentOpen((o) => !o)}>
            <span>Skill file</span>
            <svg
              className={`skill-detail-collapse-icon${contentOpen ? ' open' : ''}`}
              width="14" height="14" viewBox="0 0 16 16" fill="none"
            >
              <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {contentOpen && (
            <div className="skill-detail-md-content">
              {contentLoading && (
                <>
                  <p className="skill-detail-md-loading">Loading skill file…</p>
                  <SkeletonCodeBlock />
                </>
              )}
              {!contentLoading && skillContent === '' && <p className="skill-detail-md-loading">Content not available.</p>}
              {!contentLoading && skillContent && (
                <div className="skill-detail-md-wrapper">
                  <div className="skill-detail-md-toolbar">
                    <div className="skill-detail-view-toggle">
                      <button
                        className={`skill-detail-view-btn${viewMode === 'preview' ? ' active' : ''}`}
                        onClick={() => setViewMode('preview')}
                      >
                        Preview
                      </button>
                      <button
                        className={`skill-detail-view-btn${viewMode === 'raw' ? ' active' : ''}`}
                        onClick={() => setViewMode('raw')}
                      >
                        Raw
                      </button>
                    </div>
                    <CopyButton text={skillContent} label="Copy SKILL.md" />
                  </div>
                  {viewMode === 'preview' ? (
                    <div className="skill-detail-md-rendered">
                      {(() => {
                        const { frontmatter, body } = parseFrontmatter(skillContent);
                        return (
                          <>
                            {frontmatter && (
                              <pre className="skill-detail-frontmatter">{frontmatter}</pre>
                            )}
                            <ReactMarkdown>{body}</ReactMarkdown>
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <pre className="skill-detail-md-raw">{skillContent}</pre>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        <section className="skill-detail-section skill-detail-install">
          <h2 className="skill-detail-section-title">How to acquire this skill</h2>
          <p className="skill-detail-install-intro">
            Install this skill into your AI coding environment. Select your harness below.
          </p>

          <div className="skill-detail-install-tabs">
            {[
              { id: 'claude-code', label: 'Claude Code' },
              { id: 'cline', label: 'Cline' },
              { id: 'kilo-code', label: 'Kilo Code' },
              { id: 'continue', label: 'Continue' },
              { id: 'codex', label: 'Codex' },
              { id: 'opencode', label: 'OpenCode' },
            ].map((tab) => (
              <button
                key={tab.id}
                className={`skill-detail-install-tab${installTab === tab.id ? ' active' : ''}`}
                onClick={() => setInstallTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="skill-detail-install-methods">
            {installTab === 'claude-code' && (
              <div className="skill-detail-install-method">
                <h3>Marketplace (recommended)</h3>
                <p>
                  First, install the CA marketplace if you haven't already:
                </p>
                <CodeBlock code="/plugin marketplace add https://bitbucket.org/ca-labs/claude-code-marketplace.git" />
                <p>
                  Then install this skill directly by name:
                </p>
                <CodeBlock code={`/plugin install ${skill.id}@ca-claude-code-marketplace`} />
                <p>
                  Or run <code>/plugin</code> to open the marketplace and browse all available skills.
                </p>
                <h3>npx skills</h3>
                <CodeBlock code={`npx skills add https://bitbucket.org/ca-labs/claude-code-marketplace.git --skill ${skill.id} --agent claude-code`} />
              </div>
            )}

            {installTab === 'cline' && (
              <div className="skill-detail-install-method">
                <h3>npx skills</h3>
                <CodeBlock code={`npx skills add https://bitbucket.org/ca-labs/claude-code-marketplace.git --skill ${skill.id} --agent cline`} />
                <p>
                  Installs to <code>.agents/skills/{skill.id}/</code> in your project.
                </p>
                <h3>Manual</h3>
                <p>
                  Copy the skill body from the "Skill file" section above (skip the YAML frontmatter)
                  and save it to:
                </p>
                <CodeBlock code={`.agents/skills/${skill.id}/SKILL.md`} />
              </div>
            )}

            {installTab === 'kilo-code' && (
              <div className="skill-detail-install-method">
                <h3>npx skills</h3>
                <CodeBlock code={`npx skills add https://bitbucket.org/ca-labs/claude-code-marketplace.git --skill ${skill.id} --agent kilo`} />
                <p>
                  Installs to <code>.kilocode/skills/{skill.id}/</code> in your project.
                </p>
                <h3>Manual</h3>
                <p>
                  Copy the skill body from the "Skill file" section above (skip the YAML frontmatter)
                  and save it to:
                </p>
                <CodeBlock code={`.kilocode/skills/${skill.id}/SKILL.md`} />
              </div>
            )}

            {installTab === 'continue' && (
              <div className="skill-detail-install-method">
                <h3>npx skills</h3>
                <CodeBlock code={`npx skills add https://bitbucket.org/ca-labs/claude-code-marketplace.git --skill ${skill.id} --agent continue`} />
                <p>
                  Installs to <code>.continue/skills/{skill.id}/</code> in your project.
                </p>
                <h3>Manual</h3>
                <p>
                  Copy the skill body from the "Skill file" section above (skip the YAML frontmatter)
                  and save it to:
                </p>
                <CodeBlock code={`.continue/skills/${skill.id}/SKILL.md`} />
              </div>
            )}

            {installTab === 'codex' && (
              <div className="skill-detail-install-method">
                <h3>npx skills</h3>
                <CodeBlock code={`npx skills add https://bitbucket.org/ca-labs/claude-code-marketplace.git --skill ${skill.id} --agent codex`} />
                <p>
                  Installs to <code>.agents/skills/{skill.id}/</code> in your project.
                </p>
                <h3>Manual</h3>
                <p>
                  Copy the skill body from the "Skill file" section above (skip the YAML frontmatter)
                  and save it to:
                </p>
                <CodeBlock code={`.agents/skills/${skill.id}/SKILL.md`} />
              </div>
            )}

            {installTab === 'opencode' && (
              <div className="skill-detail-install-method">
                <h3>npx skills</h3>
                <CodeBlock code={`npx skills add https://bitbucket.org/ca-labs/claude-code-marketplace.git --skill ${skill.id} --agent opencode`} />
                <p>
                  Installs to <code>.agents/skills/{skill.id}/</code> in your project.
                </p>
                <h3>Manual</h3>
                <p>
                  Copy the skill body from the "Skill file" section above (skip the YAML frontmatter)
                  and save it to:
                </p>
                <CodeBlock code={`.agents/skills/${skill.id}/SKILL.md`} />
              </div>
            )}
          </div>
        </section>

        {hasRelated && (
          <section className="skill-detail-section">
            <h2 className="skill-detail-section-title">Related</h2>
            <div className="skill-detail-related-grid">
              {relatedSkills.map((s) => <SkillCard key={s.id} skill={s} />)}
              {relatedMcp.map((s) => <McpServerCard key={s.id} server={s} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
